import { Form, FormItemProps, FormProps, Space, Switch, Typography } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { useForm } from "antd/lib/form/Form";
import { observer } from "mobx-react";
import { Utilities } from "msg-ui-react/dist/utils/Utilities";
import React, { PropsWithChildren, ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { Component, ComponentConfig, ComponentConfigsMap, extractValueOrFunction, Properties, ValueOrFunction } from "./ComponentsMap";

export interface FormProperties<T extends Properties = Properties> {
    form?: Omit<FormProps<Partial<T>>, "form" | "onValuesChange">;
    formItem?: FormItemProps;
}

interface ObjectFormProperties<P extends Properties, O = undefined> {
    values: P;
    setValues: (values: Partial<P>) => void;
    components: ComponentConfigsMap<P, O>;
    properties?: FormProperties<P>;
    optional?: O;
    onChangeVisibleFormItems?: (visible: number) => void,
    form?: FormInstance<P>
}

/**
 * Antd form in which some or all properties of an object are mapped to react components
 */
const ObjectForm = <P extends Properties, O = undefined>({
    values,
    setValues,
    components,
    properties = {},
    optional,
    onChangeVisibleFormItems,
    form: customForm
}: PropsWithChildren<ObjectFormProperties<P, O>>): ReactElement | null => {

    const [form] = useForm<P>(customForm);
    const [visibleCount, setVisibleCount] = useState<number>(0);
    const delaysRef = useRef<Map<keyof P, number>>(new Map());

    useEffect(() => {
        if (onChangeVisibleFormItems)
            onChangeVisibleFormItems(visibleCount);
    }, [visibleCount])

    useEffect(() => {
        form.setFieldsValue(values as any);
    }, [values, form]);

    const update = (changed: Partial<P>, state: P) => {

        // Each property can have a different delay value, therefore they have to be processed separately
        Object.keys(changed).forEach(key => {
            const property = key as keyof P;
            const partial: Partial<P> = {};
            partial[property] = changed[property];

            // Clear pending update
            if (delaysRef.current.has(property))
                window.clearTimeout(delaysRef.current.get(property));

            const delay = components[property]?.delay;
            if (delay) {
                // Update after delay
                delaysRef.current.set(property, window.setTimeout(() => setValues(partial), delay));
            }
            else {
                // Update immediatly
                delaysRef.current.delete(property);
                setValues(partial);
            }
        })
    };

    const getFormItems = (
        map: ComponentConfigsMap<P, O>,
        values: P,
        namePath: string[] = [],
        indexPath: number[] = []
    ) => {
        let result: JSX.Element[] = [];
        let newVisibleCount = 0;

        const getFormItem = (
            index: number,
            label: string,
            property: string,
            config: ComponentConfig<P, keyof P, O>,
            content: ReactNode,
            valuePropName?: string
        ) => {
            newVisibleCount++;
            return (
                <Form.Item
                    key={[...indexPath, index].join("-")}
                    name={[...namePath, property]}
                    label={
                        <Typography.Text ellipsis style={{ fontSize: "13px", fontWeight: "500" }}>
                            {label}
                        </Typography.Text>
                    }
                    {...properties.formItem}
                    {...config.formItem}
                    valuePropName={config.formItem?.valuePropName ?? properties.formItem?.valuePropName ?? valuePropName}
                >
                    {content}
                </Form.Item>
            );
        };

        const getCustomElement = (
            index: number,
            element: ValueOrFunction<ReactNode, P, O>,
            suffix: string
        ) => {
            return (
                <React.Fragment key={`${[...indexPath, index].join("-")}-${suffix}`}>
                    {extractValueOrFunction(element, values, optional, form)}
                </React.Fragment>
            );
        };

        Object.keys(map).forEach((property, index) => {
            const config = map[property];
            if (config) {
                const visible = !config.hidden ||
                    (typeof config.hidden !== "boolean" &&
                        !config.hidden(values, optional, form));

                const label = !config.label ?
                    Utilities.splitCamelCase(property[0].toUpperCase() + property.slice(1)) :
                    (typeof config.label == 'string') ? config.label : config.label(values, optional, form);

                if (visible) {
                    if (config.before)
                        result.push(getCustomElement(index, config.before, "before"));

                    const component = extractValueOrFunction(config.component, values, optional, form);
                    if (React.isValidElement(component)) {
                        result.push(
                            getFormItem(index, label, property, config, component, getValuePropName(component))
                        );
                    } else {
                        const map = component as ComponentConfigsMap<P, O>;
                        const children = getFormItems(
                            map,
                            values[property],
                            [...namePath, property],
                            [...indexPath, index]
                        );

                        if (config.group) {
                            const content = <Space {...config.group}>{children}</Space>;
                            result.push(getFormItem(index, label, property, config, content));
                        } else {
                            result = result.concat(children);
                        }
                    }

                    if (config.after)
                        result.push(getCustomElement(index, config.after, "after"));
                }
            }
        });

        if (onChangeVisibleFormItems && newVisibleCount != visibleCount)
            setVisibleCount(newVisibleCount);

        return result;
    };

    return (
        <Form form={form} onValuesChange={update} {...properties.form}>
            {getFormItems(components, values)}
        </Form>
    );
};

export default observer(ObjectForm);

const getValuePropName = (component: Component) => {
    if (component.type === Switch) return "checked";
    return "value";
};
